// src/hooks/User/useUserOperations.js
import { useState } from 'react';
import { Create_User, Update_User, Update_User_Org, Delete_User, Delete_User_Org, Set_Password } from '../../services/oprations/Users';

export const useUserOperations = (dispatch, toast, refreshUsers, currentUser, managers) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [isSetPasswordModalOpen, setIsSetPasswordModalOpen] = useState(false);
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const [userToUpdate, setUserToUpdate] = useState(null);
  const [userForPassword, setUserForPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  // eslint-disable-next-line
  const [errors, setErrors] = useState({});

  const [createFormData, setCreateFormData] = useState({
    user_name: '', user_email: '', user_phone: '', user_department: '',
    user_roll: '', manager_Id: '', shift_start: '09:00:00', shift_end: '18:00:00', 
    timezone: 'Asia/Calcutta',workoffset:"00:00:00", viewtimezone: 'Asia/Calcutta'
  });

  const [updateFormData, setUpdateFormData] = useState({
    user_name: '', user_email: '', user_phone: '', user_department: '',
    user_roll: '', shift_start: '', shift_end: '', timezone: '',workoffset:'', 
    viewtimezone: '', status: 'ACTIVE'
  });

  const handleCreateClick = () => {
    setCreateFormData({ 
      user_name: '', user_email: '', user_phone: '', user_department: '', 
      user_roll: '', manager_Id: '', shift_start: '09:00:00', shift_end: '18:00:00', 
      timezone: 'Asia/Calcutta', viewtimezone: 'Asia/Calcutta' 
    });
    setIsCreateModalOpen(true);
  };

  const handleCreateInputChange = (e) => {
    const { name, value } = e.target;
    setCreateFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (currentUser?.role === 'Organization' && 
        (!createFormData.user_name || !createFormData.user_roll || 
         !createFormData.shift_start || !createFormData.shift_end || 
         !createFormData.manager_Id)) {
      toast.error('User Name, ID, Shift Start, Shift End and Manager are required');
      return;
    }
    
    setIsCreating(true);
    const res = await dispatch(Create_User({ ...createFormData }));
    if (res?.data?.success) {
      setIsCreateModalOpen(false);
      toast.success('User created successfully');
      await refreshUsers();
    } else {
      toast.error(res?.data?.message || 'Failed to create user');
    }
    setIsCreating(false);
  };

  const handleUpdateClick = (e, user) => {
    e.stopPropagation();
    setUserToUpdate(user);
    setUpdateFormData({
      user_name: user.user_name || '',
      user_email: user.user_email || '',
      user_phone: user.user_phone || '',
      user_department: user.user_department || '',
      user_roll: user.user_roll || '',
      shift_start: user?.shift_start || '09:00:00',
      shift_end: user?.shift_end || '18:00:00',
      timezone: user?.timezone || 'Asia/Calcutta',
      workoffset:user?.workoffset || '+00:00:00',
      viewtimezone: user?.viewtimezone || 'Asia/Calcutta',
      status: user.status || 'ACTIVE'
    });
    setIsUpdateModalOpen(true);
  };

  const handleUpdateInputChange = (e) => {
    const { name, value } = e.target;
    setUpdateFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    if (!userToUpdate) return;
    
    const res = currentUser?.role === 'Manager' 
      ? await dispatch(Update_User(updateFormData, userToUpdate._id))
      : await dispatch(Update_User_Org(updateFormData, userToUpdate._id));
    
    if (res?.data?.success) {
      setIsUpdateModalOpen(false);
      setUserToUpdate(null);
      toast.success('User updated successfully');
      await refreshUsers();
    } else {
      toast.error('Failed to update user');
    }
  };

  const closeUpdateModal = () => {
    setIsUpdateModalOpen(false);
    setUserToUpdate(null);
    setUpdateFormData({ 
      user_name: '', user_email: '', user_phone: '', user_department: '',
      user_roll: '', shift_start: '', shift_end: '', timezone: '', 
      viewtimezone: '', status: 'ACTIVE' 
    });
  };

  const handleSetPasswordClick = (e, user) => {
    e.stopPropagation();
    setUserForPassword(user);
    setNewPassword('');
    setShowPassword(false);
    setIsSetPasswordModalOpen(true);
  };

  const handleSetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!userForPassword || !newPassword.trim()) {
      toast.error('Please enter a password');
      return;
    }
    
    setIsSettingPassword(true);
    try {
      const res = await dispatch(Set_Password(userForPassword._id, newPassword));
      if (res?.data?.success) {
        setIsSetPasswordModalOpen(false);
        setUserForPassword(null);
        setNewPassword('');
        toast.success(res?.data?.message || 'Password updated successfully');
      } else {
        toast.error(res?.data?.message || 'Failed to update password');
      }
    } catch (error) {
      toast.error(error?.message || 'Failed to update password');
    } finally {
      setIsSettingPassword(false);
    }
  };

  const closeSetPasswordModal = () => {
    setIsSetPasswordModalOpen(false);
    setUserForPassword(null);
    setNewPassword('');
    setShowPassword(false);
  };

  const handleDeleteConfirm = async (userToDelete) => {
    if (!userToDelete) return;
    
    const res = currentUser?.role === 'Organization' 
      ? await dispatch(Delete_User_Org(userToDelete._id))
      : await dispatch(Delete_User(userToDelete._id));
    
    if (res?.data?.success) {
      toast.success(res?.data?.message);
      await refreshUsers();
      return true;
    } else {
      toast.error(res?.data?.message);
      return false;
    }
  };

  return {
    // Create User
    isCreateModalOpen,
    setIsCreateModalOpen,
    isCreating,
    createFormData,
    handleCreateInputChange,
    handleCreateSubmit,
    handleCreateClick,
    
    // Update User
    isUpdateModalOpen,
    setIsUpdateModalOpen,
    userToUpdate,
    updateFormData,
    handleUpdateInputChange,
    handleUpdateSubmit,
    handleUpdateClick,
    closeUpdateModal,
    
    // Set Password
    isSetPasswordModalOpen,
    setIsSetPasswordModalOpen,
    userForPassword,
    newPassword,
    setNewPassword,
    showPassword,
    setShowPassword,
    isSettingPassword,
    handleSetPasswordClick,
    handleSetPasswordSubmit,
    closeSetPasswordModal,
    
    // Delete User
    handleDeleteConfirm,
    
    // Errors
    errors
  };
};
